// Scheduled news, era transitions, achievements (DESIGN §1, §7, §9.1 tick order).
import { withAchBatch } from '../data/achWells.js';
import { newsForDay, applyNewsEffect } from '../data/news.js';
import { ACHIEVEMENTS } from '../data/achievements.js';
import { ERAS, ERA_BY_ID, eraForDay } from '../data/eras.js';
import { EQUIPMENT, FIELDS } from './defs.js';
import { dayToDate } from '../core/time.js';
import { netWorth, netWorth2025 } from './finance.js';
import { grant } from '../economy/tokens.js';
import { notify } from './util.js';
import { applyM2NewsEffect } from './m2/index.js';
import { applyM3EconNewsEffect } from './m3/econtick.js'; // M3 econ
import { applyM4WorldNewsEffect } from './m4/worldHooks.js'; // M4-C

export const ERA_TOKEN_GRANT = 50;
export const ACHIEVEMENT_TOKENS = 10;

export function tickNews(state) {
  for (const item of newsForDay(state.day)) {
    if (state.newsFired[item.id]) continue;
    state.newsFired[item.id] = state.day;
    applyNewsEffect(state, item);
    applyM2NewsEffect(state, item); // regionEvent (sabotage, attack, occupation, threat, expropriate)
    applyM3EconNewsEffect(state, item); // M3 econ: nationalization, leaseRound, embargo, wellFires, regime, suez, route
    applyM4WorldNewsEffect(state, item); // M4-C: storms, lease rounds, megaprojects, sanctions, regimes, freeze, wildfire (inert < 2000)
    const n = { id: item.id, day: state.day, text: item.headline, kind: item.kind, fieldId: item.fieldId ?? null, regionId: item.regionId ?? null };
    state.news.push(n);
    if (state.news.length > 400) state.news.splice(0, state.news.length - 300);
    notify(state, 'news', { ...n, pause: !!item.pause, level: item.pause ? 'major' : 'news' });
  }
}

export function tickEra(state) {
  const era = eraForDay(state.day);
  if (!era || era.id === state.eraId) return;
  const from = ERA_BY_ID[state.eraId];
  if (from && era.num < from.num) return;
  const summary = eraSummary(state, from, era);
  state.eraId = era.id;
  state.flags.eraSummaryShown[era.id] = state.day;
  grant(state, ERA_TOKEN_GRANT, 'era', era.id);
  notify(state, 'era', { pause: true, level: 'major', eraId: era.id, fromEraId: from?.id ?? null, summary, text: `A new era begins: ${era.name} (${era.roman ?? era.num}).` });
}

export function eraSummary(state, from, to) {
  const st = state.company.stats;
  const startDay = from?.startDay ?? 0;
  const news = state.news.filter((n) => n.day >= startDay && ['discovery', 'market', 'disaster', 'technology'].includes(n.kind));
  const top = news.filter((n) => /Drake|Empire|Pithole|Standard|Spindletop|Bradford|Frasch|crash|Gush/i.test(n.text)).slice(0, 3);
  const y0 = to?.startYear ?? dayToDate(state.day).year;
  return {
    fromEra: from ? { id: from.id, name: from.name } : null,
    toEra: to ? { id: to.id, name: to.name, blurb: to.blurb, roman: to.roman } : null,
    year: dayToDate(state.day).year,
    company: state.company.name,
    cash: Math.round(state.company.cash),
    netWorth: Math.round(netWorth(state)),
    netWorth2025: Math.round(netWorth2025(state)),
    wellsDrilled: st.wellsDrilled, strikes: st.strikes, dryHoles: st.dryHoles, gushers: st.gushers,
    bblProduced: Math.round(st.bblProduced), bblSold: Math.round(st.bblSold), ftDrilled: Math.round(st.ftDrilled),
    deaths: st.deaths, injuries: st.injuries, reputation: Math.round(state.company.reputation),
    achievements: Object.keys(state.achievements).length,
    topNews: (top.length ? top : news.slice(-3)).map((n) => ({ day: n.day, text: n.text })),
    newItems: Object.values(EQUIPMENT).filter((d) => d.year >= y0 && d.year < y0 + 10).slice(0, 8).map((d) => ({ id: d.id, name: d.name, year: d.year })),
    newFields: Object.values(FIELDS).filter((f) => { const y = dayToDate(f.opensDay ?? 0).year; return y >= y0 && y < y0 + 10; }).map((f) => ({ id: f.id, name: f.name })),
  };
}

export function tickAchievements(state) {
  // polish (G7): a Quick Start company inherits milestones that are already true on day 1 (a producing well, a
  // waterflood…). Baseline them silently on the first tick; they can unlock later once they go false and come true again.
  const flags = (state.flags ??= {});
  const base = (state.startYear ?? 1859) > 1859 ? (flags.achBaseline ??= { pending: true }) : null;
  const first = !!base?.pending;
  if (base) delete base.pending;
  withAchBatch(state, () => checkAchievements(state, base, first));
}
function checkAchievements(state, base, first) {
  for (const a of ACHIEVEMENTS) {
    if (state.achievements[a.id] != null) continue;
    let ok = false;
    try { ok = !!a.check(state); } catch { ok = false; }
    if (base) {
      if (first && ok) { base[a.id] = true; continue; }
      if (base[a.id]) { if (!ok) delete base[a.id]; continue; }
    }
    if (!ok) continue;
    state.achievements[a.id] = state.day;
    grant(state, a.tokens ?? ACHIEVEMENT_TOKENS, 'achievement', a.id);
    notify(state, 'achievement', { id: a.id, name: a.name, text: `Achievement unlocked: ${a.name} — ${a.desc}`, level: 'major' });
  }
}
